import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { BehaviorSubject, filter, retry, take, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { accessTokenStore } from '../core/auth/access-token.store';
import { deepLinkFor } from '../shared/utils/push-deep-link.util';
import { NotificationSoundService } from './notification-sound.service';

type FcmData = Record<string, string> & {
    type?: string;
    bookingId?: string;
    /** Coupon alerts (`type: 'coupon'`) carry this and NO `bookingId`. */
    couponId?: string;
    notificationId?: string;
    status?: string;
    url?: string;
};

@Injectable({
    providedIn: 'root'
})
export class MessagingService {
    currentMessage = new BehaviorSubject<any>(null);
    private fcmToken = new BehaviorSubject<string | null>(null);
    fcmToken$ = this.fcmToken.asObservable();
    /**
     * BLK-37: the token WITHOUT the initial `null`.
     *
     * `fcmToken$` is a BehaviorSubject seeded with null, so `take(1)` on it
     * returns whatever is there RIGHT NOW — which on a first login, or any cold
     * load where the user types faster than FCM responds, is null. That sent
     * `fcmTokens: []`, the backend no-opped, and the shop silently stopped
     * receiving booking alerts. Subscribe to this instead and you are
     * guaranteed a real token, whenever it arrives.
     */
    fcmTokenReady$ = this.fcmToken$.pipe(
        filter((token): token is string => !!token),
    );
    private swClickListenerAttached = false;
    private lastRegisteredToken: string | null = null;

    /** The token if FCM has already answered, else null. Never blocks. */
    get fcmTokenValue(): string | null {
        return this.fcmToken.value;
    }

    constructor(
        private angularFireMessaging: AngularFireMessaging,
        private router: Router,
        private zone: NgZone,
        private sound: NotificationSoundService,
        private http: HttpClient,
    ) {
        this.angularFireMessaging.messages.subscribe(
            (messaging: any) => {
                messaging.onMessage = messaging.onMessage.bind(messaging);
                messaging.onTokenRefresh = messaging.onTokenRefresh.bind(messaging);
            }
        );
        this.attachServiceWorkerClickListener();

        // BLK-37: FCM rotates tokens (new browser profile, cleared site data,
        // push-service migration). Nothing was listening, so a rotated token was
        // never delivered to the server and the shop went quiet with no error
        // anywhere. `tokenChanges` was already available on the compat API and
        // simply unused.
        //
        // This only CACHES the token. Registration is deliberately not fired
        // from here — see registerTokenWithBackend's auth gate for why.
        this.angularFireMessaging.tokenChanges.subscribe({
            next: (token) => {
                if (!token) return;
                this.fcmToken.next(token);
                // A rotation mid-session must still reach the server, but only
                // when there IS a session; on the login page this is a no-op.
                if (token !== this.lastRegisteredToken) {
                    this.registerTokenWithBackend(token);
                }
            },
            error: (err) => console.error('FCM token refresh failed.', err),
        });

        // THE OTHER HALF of the same problem. `tokenChanges` covers "the FCM
        // token changed while signed in"; this covers "we became signed in
        // while holding an FCM token".
        //
        // Registration used to run from ONE place: the login success handler.
        // But on a cold load the access token is restored asynchronously from
        // the refresh cookie, long after `tokenChanges` has emitted and been
        // dropped by the auth gate — so a vendor who simply stays logged in
        // never re-registered. Once their token rotated or was never captured,
        // that shop stopped receiving booking pushes, permanently and silently.
        //
        // Keyed on the token STORE rather than on login, so session restore and
        // refresh-rotation are covered by the same signal.
        accessTokenStore.onChange((accessToken) => {
            if (!accessToken) {
                // Signed out: forget what we registered so the next sign-in
                // re-sends, even if FCM hands back the same token.
                this.lastRegisteredToken = null;
                return;
            }
            const fcmToken = this.fcmToken.value;
            if (fcmToken && fcmToken !== this.lastRegisteredToken) {
                this.registerTokenWithBackend(fcmToken);
            }
        });
    }

    requestPermission() {
        this.angularFireMessaging.requestToken.subscribe(
            (token) => {
                this.fcmToken.next(token);
            },
            (err) => {
                console.error('Unable to get permission to notify.', err);
            }
        );
    }

    /**
     * BLK-37: register the token against the signed-in office, out of band from
     * login. `POST /auth/office/fcm-token` already existed and was documented
     * for exactly this ("Call after vendor login when Firebase Messaging returns
     * a token (often async)") — only this call was missing.
     *
     * Sends the SINGLE `fcmToken` field on purpose: the backend treats that as
     * replace-not-merge, which drops stale tokens from old sessions. Passing
     * `fcmTokens[]` would merge and let dead tokens accumulate forever.
     *
     * The auth interceptor adds the bearer token, the office audience header and
     * the 401-refresh-retry, so a plain post is correctly authenticated.
     */
    registerTokenWithBackend(token?: string | null): void {
        const fcmToken = token ?? this.fcmToken.value;
        if (!fcmToken) return;

        // MUST be signed in. `tokenChanges` emits at BOOTSTRAP — @angular/fire's
        // compat stream is `concat(getToken, tokenChange$)`, and `getToken`
        // resolves a real token silently whenever notification permission is
        // already granted, i.e. for every returning vendor, including on the
        // login page.
        //
        // Firing an unauthenticated POST there is not merely wasteful: this
        // route is NOT in the interceptor's auth-exempt list, so the 401 is read
        // as an expired session — refreshSession() runs, fails (there is no
        // valid refresh cookie, which is why they are on the login page) and
        // clearAuthAndRedirect() navigates to a bare /auth/login, DISCARDING the
        // ?redirectTo the guard put there. A vendor who tapped a booking push
        // would log in and land on the list instead of that booking. Worse, if
        // the doomed refresh resolves just after a successful login,
        // clearAuthSession() wipes the session that was just established.
        if (!accessTokenStore.get()) return;
        this.http
            .post(`${environment.baseUrl}/auth/office/fcm-token`, { fcmToken })
            // A dropped registration is invisible AND permanent: the shop just
            // stops receiving bookings, with nothing on screen to explain it.
            // Retry the transient case (network blip, a 401 racing the refresh
            // at login) instead of losing it on the first failure.
            .pipe(retry({ count: 3, delay: (_err, n) => timer(n * 2000) }))
            .subscribe({
                next: () => {
                    this.lastRegisteredToken = fcmToken;
                },
                // Still never surfaced to the vendor: this must not block or
                // alarm someone who has just logged in successfully. But make
                // the message searchable — after 3 retries the shop IS
                // unreachable by push, and this line is the only trace.
                error: (err) =>
                    console.error(
                        '[FCM] registration FAILED after retries — this vendor will NOT receive booking pushes.',
                        err,
                    ),
            });
    }

    /**
     * Call after login. If the token is already in hand this registers at once;
     * if FCM has not answered yet it registers the moment it does.
     */
    registerTokenWhenReady(): void {
        // Called from handleLoginSuccess, i.e. after applyLoginResponse has put
        // the access token in the store — so the gate in
        // registerTokenWithBackend passes.
        if (this.fcmToken.value) {
            this.registerTokenWithBackend(this.fcmToken.value);
            return;
        }
        this.fcmTokenReady$
            .pipe(take(1))
            .subscribe((token) => this.registerTokenWithBackend(token));
    }

    receiveMessage() {
        this.angularFireMessaging.messages.subscribe(
            (payload: any) => {
                console.log('new message received. ', payload);
                this.currentMessage.next(payload);
                this.sound.play();
                this.showNotification(payload);
            })
    }

    showNotification(payload: any) {
        const notificationTitle = payload.notification?.title || 'New Message';
        const data: FcmData = payload.data || {};
        const clickUrl = this.buildDeepLinkFromData(data);
        // TS 5.4's DOM lib dropped `vibrate` from NotificationOptions (it is a
        // legacy, still widely-shipped hint). The value and the runtime call are
        // unchanged — only the declared type is widened so the build type-checks.
        const notificationOptions: NotificationOptions & { vibrate?: number[] } = {
            body: payload.notification?.body || '',
            icon: '/favicon.ico',
            // Hint to the UA that the notification should not be silent.
            // Note: sound delivery is ultimately controlled by the OS/browser.
            silent: false,
            vibrate: [200, 100, 200],
            data: { ...data, clickUrl },
        };

        console.log('--- FCM Notification Delivery Attempt ---');
        console.log('Permission:', Notification.permission);
        console.log('Document visibility:', document.hidden ? 'hidden (Background)' : 'visible (Foreground)');

        if (Notification.permission !== 'granted') {
            return;
        }

        // Prefer the service worker path so the same notificationclick handler
        // in firebase-messaging-sw.js picks it up and posts back to the app.
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then(registration => {
                registration.showNotification(notificationTitle, notificationOptions);
            });
            return;
        }

        // Fallback for environments without a service worker: wire up onclick
        // ourselves since there's no SW notificationclick handler to help.
        const n = new Notification(notificationTitle, notificationOptions);
        n.onclick = (event) => {
            event.preventDefault();
            window.focus();
            n.close();
            this.zone.run(() => this.router.navigateByUrl(clickUrl));
        };
    }

    // Listen for clicks the service worker forwards from notificationclick,
    // and also for sound requests it emits on background pushes.
    private attachServiceWorkerClickListener() {
        if (this.swClickListenerAttached) return;
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

        navigator.serviceWorker.addEventListener('message', (event) => {
            const msg = event?.data;
            if (!msg || typeof msg !== 'object') return;

            if (msg.type === 'NOTIFICATION_CLICK') {
                const url = typeof msg.url === 'string' && msg.url ? msg.url : '/';
                this.zone.run(() => {
                    this.router.navigateByUrl(url);
                });
                return;
            }

            if (msg.type === 'NOTIFICATION_SOUND') {
                // Background push arrived and this tab is still running.
                // Play the chime so the user hears it even if OS sound is off.
                this.sound.play();
                return;
            }
        });
        this.swClickListenerAttached = true;
    }

    /**
     * The foreground half of the deep link. The rule itself lives in
     * `shared/utils/push-deep-link.util.ts` so it can be spec'd; the BACKGROUND
     * half is a copy of the same rule in `src/firebase-messaging-sw.js`
     * (`buildDeepLinkFromData` there), which is a plain worker script and
     * cannot import it.
     * **Change the two together** — a vendor tapping the same notification must
     * land in the same place whether the tab was open or not.
     */
    private buildDeepLinkFromData(data: FcmData): string {
        return deepLinkFor(data);
    }
}
